import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const VideoAdmin = () => {
  const [videos, setVideos] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', url: '', description: '', category: '', access: 'all', attachments: [] });
  const [editIndex, setEditIndex] = useState(null);
  const [files, setFiles] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=academy-videos&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setVideos)
      .catch(() => setVideos([]));
    adminFetch('/api/admin-files.php?action=list')
      .then((r) => r.ok ? r.json() : { files: [] })
      .then((d) => setFiles(d.files || []))
      .catch(() => setFiles([]));
  }, []);

  const toggleAttachment = (file) => {
    setForm((prev) => {
      const exists = prev.attachments.some((a) => a.url === file.url);
      return {
        ...prev,
        attachments: exists
          ? prev.attachments.filter((a) => a.url !== file.url)
          : [...prev.attachments, { name: file.name, url: file.url }],
      };
    });
  };

  const removeAttachment = (i) => {
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((_, idx) => idx !== i) }));
  };

  // 直接アップロード（複数可）。ファイル管理と同じ /uploads/ に保存され、一覧にも反映される
  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    const fd = new FormData();
    selected.forEach((f) => fd.append('files[]', f));
    try {
      const res = await adminFetch('/api/admin-files.php?action=upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && Array.isArray(data.files)) {
        // ファイル管理の一覧に反映（連動）
        setFiles((prev) => [...data.files, ...prev]);
        // この動画に自動添付
        setForm((prev) => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            ...data.files
              .filter((nf) => !prev.attachments.some((a) => a.url === nf.url))
              .map((nf) => ({ name: nf.name, url: nf.url })),
          ],
        }));
      }
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  const saveVideos = async (data) => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'academy-videos', data }),
      });
      const result = await res.json();
      if (result.success) {
        setVideos(data);
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch {}
    setSaving(false);
  };

  const addVideo = () => {
    if (!form.title.trim() || !form.url.trim()) return;
    const video = { ...form, id: String(Date.now()), createdAt: new Date().toISOString() };
    saveVideos([video, ...videos]);
    setForm({ title: '', url: '', description: '', category: '', access: 'all', attachments: [] });
    setShowForm(false);
    setShowPicker(false);
  };

  const updateVideo = () => {
    if (editIndex === null) return;
    const updated = [...videos];
    updated[editIndex] = { ...updated[editIndex], ...form };
    saveVideos(updated);
    setEditIndex(null);
    setForm({ title: '', url: '', description: '', category: '', access: 'all', attachments: [] });
    setShowForm(false);
    setShowPicker(false);
  };

  const deleteVideo = (index) => {
    if (!window.confirm('この動画を削除しますか？')) return;
    saveVideos(videos.filter((_, i) => i !== index));
  };

  const startEdit = (index) => {
    const v = videos[index];
    setForm({ title: v.title, url: v.url, description: v.description || '', category: v.category || '', access: v.access || 'all', attachments: v.attachments || [] });
    setEditIndex(index);
    setShowForm(true);
    setShowPicker(false);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditIndex(null);
    setForm({ title: '', url: '', description: '', category: '', access: 'all', attachments: [] });
    setShowPicker(false);
  };

  // アカデミー契約済みの顧客にメール通知
  const [notifying, setNotifying] = useState(false);
  const notifyMembers = async (video) => {
    if (!window.confirm(`「${video.title}」の公開をアカデミー契約済みの顧客に通知しますか？`)) return;
    setNotifying(true);
    try {
      // 契約済み顧客のメール取得
      const custRes = await adminFetch('/api/admin-read.php?file=customers&t=' + Date.now());
      const customers = await custRes.json();
      const emails = customers.filter((c) => c.email && (c.srwithContract === 'あり' || c.academyStatus === '受講中')).map((c) => c.email);

      if (emails.length === 0) {
        setMessage('アカデミー契約済みの顧客がいません');
        setNotifying(false);
        return;
      }

      const formData = new FormData();
      formData.append('subject', `【SrWithアカデミー】新しい動画が公開されました: ${video.title}`);
      formData.append('body', `新しい動画コンテンツが公開されました。\n\n`
        + `タイトル: ${video.title}\n`
        + (video.description ? `内容: ${video.description}\n` : '')
        + `\n以下のURLからご覧いただけます。\nhttps://srwith.com/academy/members\n`);
      formData.append('emails', JSON.stringify(emails));

      const res = await adminFetch('/api/admin-ml.php?action=send', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.sentCount}件に通知しました`);
      } else {
        setMessage('通知に失敗しました');
      }
    } catch { setMessage('通知に失敗しました'); }
    setNotifying(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">動画管理</h2>
        <button onClick={() => { setShowForm(true); setEditIndex(null); setShowPicker(false); setForm({ title: '', url: '', description: '', category: '', access: 'all', attachments: [] }); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規動画</button>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {showForm && (
        <div className="bg-white rounded-xl shadow p-6 mb-4 space-y-3">
          <h3 className="font-bold">{editIndex !== null ? '動画を編集' : '新規動画を追加'}</h3>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="タイトル" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input type="text" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="動画URL（YouTube限定公開等）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="カテゴリ（例: 勉強会, LAB, その他）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <select value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">全員公開</option>
              <option value="contract">契約者限定（SrWith + アカデミー）</option>
              <option value="academy">アカデミー限定</option>
            </select>
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
            placeholder="説明（任意）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />

          {/* 添付資料 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-bold text-gray-600">添付資料（複数選択可）</label>
              <div className="flex items-center gap-3">
                <label className={`text-xs font-bold cursor-pointer ${uploading ? 'text-gray-400' : 'text-green-600 hover:underline'}`}>
                  {uploading ? 'アップロード中...' : '⬆ 直接アップロード'}
                  <input type="file" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
                <button type="button" onClick={() => setShowPicker(!showPicker)}
                  className="text-xs font-bold text-blue-600 hover:underline">
                  {showPicker ? '閉じる' : '+ 既存から選択'}
                </button>
              </div>
            </div>

            {form.attachments.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="w-8 h-8 bg-gray-100 rounded border flex items-center justify-center shrink-0 text-[9px] font-bold text-gray-500">{a.name.split('.').pop().toUpperCase()}</span>
                    <span className="flex-1 text-sm truncate">{a.name}</span>
                    <button type="button" onClick={() => removeAttachment(i)}
                      className="text-red-500 text-xs font-bold hover:text-red-700 shrink-0">削除</button>
                  </div>
                ))}
              </div>
            )}

            {showPicker && (
              <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                {files.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">アップロード済みファイルがありません。先に「ファイル管理」からアップロードしてください</p>
                ) : (
                  files.map((file, idx) => {
                    const selected = form.attachments.some((a) => a.url === file.url);
                    const ext = file.name.split('.').pop().toUpperCase();
                    return (
                      <button key={idx} type="button" onClick={() => toggleAttachment(file)}
                        className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-3 border-b last:border-b-0 ${selected ? 'bg-blue-50' : ''}`}>
                        <div className="w-9 h-9 bg-gray-100 rounded border flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-gray-500">{ext}</span>
                        </div>
                        <span className={`flex-1 text-sm truncate ${selected ? 'font-bold text-blue-700' : 'text-gray-700'}`}>{file.name}</span>
                        {selected && <span className="text-blue-600 text-xs font-bold shrink-0">✓ 選択中</span>}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={editIndex !== null ? updateVideo : addVideo} disabled={saving || !form.title.trim() || !form.url.trim()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {editIndex !== null ? '更新' : '追加'}
            </button>
            <button onClick={cancelForm} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
          </div>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">動画がありません</div>
      ) : (
        <div className="space-y-3">
          {videos.map((v, i) => (
            <div key={v.id || i} className="bg-white rounded-xl shadow p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-sm">{v.title}</h3>
                    {v.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">{v.category}</span>}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${{ all: 'bg-gray-100 text-gray-600', contract: 'bg-green-100 text-green-700', academy: 'bg-emerald-100 text-emerald-700' }[v.access || 'all']}`}>
                      {{ all: '全員公開', contract: '契約者限定', academy: 'アカデミー限定' }[v.access || 'all']}
                    </span>
                  </div>
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">{v.url}</a>
                  {v.description && <p className="text-xs text-gray-500 mt-1">{v.description}</p>}
                  {v.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {v.attachments.map((a, i) => (
                        <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded hover:bg-gray-200">
                          📎 {a.name}
                        </a>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">{formatDate(v.createdAt)}</p>
                </div>
                <div className="flex gap-1.5 shrink-0 ml-3">
                  <button onClick={() => notifyMembers(v)} disabled={notifying}
                    className="bg-green-100 text-green-700 px-3 py-1 rounded text-[10px] font-bold hover:bg-green-200 disabled:opacity-50">通知</button>
                  <button onClick={() => startEdit(i)}
                    className="bg-gray-100 text-gray-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-gray-200">編集</button>
                  <button onClick={() => deleteVideo(i)}
                    className="bg-red-50 text-red-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoAdmin;
